<template>
  <div class="card">
    <div class="card__head">
      <div>
        <h2 class="card__title">{{ t("editor.listing") }}</h2>
        <p class="card__sub">{{ t("editor.listingLede") }}</p>
      </div>
      <span v-if="locale === 'en'" :class="['badge', gaps.length ? 'badge--warn' : 'badge--success']">
        {{ gaps.length ? t("listing.incomplete", { count: gaps.length }) : t("listing.complete") }}
      </span>
    </div>

    <nav class="tabs" role="tablist">
      <button
        v-for="code in LISTING_LOCALES"
        :key="code"
        type="button"
        role="tab"
        :aria-selected="locale === code"
        :class="['tabs__tab', { 'tabs__tab--on': locale === code }]"
        @click="locale = code"
      >
        {{ t(`listing.locales.${code}`) }}
      </button>
    </nav>

    <p class="small muted">{{ locale === "en" ? t("listing.requiredNote") : t("listing.optionalNote") }}</p>

    <div v-if="error" class="notice notice--danger" style="margin-bottom: var(--space-4)"><div class="notice__body">{{ error }}</div></div>

    <div class="field">
      <label class="field__label" :for="`l-tagline-${locale}`">{{ t("listing.fields.tagline") }} <span v-if="required" class="req">*</span></label>
      <input :id="`l-tagline-${locale}`" v-model.trim="form.tagline" class="input" maxlength="200" />
    </div>
    <div class="field">
      <label class="field__label" :for="`l-desc-${locale}`">{{ t("listing.fields.description") }} <span v-if="required" class="req">*</span></label>
      <textarea :id="`l-desc-${locale}`" v-model="form.description" class="textarea" rows="6"></textarea>
    </div>

    <div class="field">
      <span class="field__label">{{ t("listing.fields.screenshots") }} <span v-if="required" class="req">*</span></span>
      <span class="field__hint">{{ t("listing.screenshotsHint", { max: MAX_SHOTS }) }}</span>
      <div v-for="(shot, i) in form.screenshots" :key="`${locale}-${i}`" class="row listing__shot">
        <ImageUpload v-model="form.screenshots[i]" kind="screenshot" :app-id="appId" :clearable="false"
                     :label="t('listing.screenshotN', { n: i + 1 })" class="listing__upload" />
        <button type="button" class="btn btn--ghost btn--sm" @click="form.screenshots.splice(i, 1)">{{ t("common.remove") }}</button>
      </div>
      <button type="button" class="btn btn--secondary btn--sm" :disabled="form.screenshots.length >= MAX_SHOTS"
              @click="form.screenshots.push('')">
        {{ t("listing.addScreenshot") }}
      </button>
    </div>

    <div class="grid grid--2">
      <div class="field">
        <label class="field__label" :for="`l-privacy-${locale}`">{{ t("listing.fields.privacy_url") }} <span v-if="required" class="req">*</span></label>
        <input :id="`l-privacy-${locale}`" v-model.trim="form.privacy_url" class="input" type="url" placeholder="https://"
               :class="{ 'input--invalid': form.privacy_url && !isHttps(form.privacy_url) }" />
      </div>
      <div class="field">
        <label class="field__label" :for="`l-support-${locale}`">{{ t("listing.fields.support_url") }} <span v-if="required" class="req">*</span></label>
        <input :id="`l-support-${locale}`" v-model.trim="form.support_url" class="input" type="url" placeholder="https://"
               :class="{ 'input--invalid': form.support_url && !isHttps(form.support_url) }" />
      </div>
      <div class="field">
        <label class="field__label" :for="`l-docs-${locale}`">{{ t("listing.fields.docs_url") }}</label>
        <input :id="`l-docs-${locale}`" v-model.trim="form.docs_url" class="input" type="url" placeholder="https://" />
      </div>
    </div>

    <div class="field">
      <label class="field__label" :for="`l-ret-${locale}`">{{ t("listing.fields.data_retention_text") }} <span v-if="required" class="req">*</span></label>
      <textarea :id="`l-ret-${locale}`" v-model="form.data_retention_text" class="textarea" rows="3"></textarea>
      <span class="field__hint">{{ t("listing.retentionHint") }}</span>
    </div>
    <div class="field">
      <label class="field__label" :for="`l-price-${locale}`">{{ t("listing.fields.pricing_text") }}</label>
      <textarea :id="`l-price-${locale}`" v-model="form.pricing_text" class="textarea" rows="2"></textarea>
      <span class="field__hint">{{ t("editor.pricingHint") }}</span>
    </div>

    <div v-if="locale === 'en' && gaps.length" class="notice notice--warn" style="margin-bottom: var(--space-4)">
      <div class="notice__body">
        {{ t("listing.missing") }}
        <ul><li v-for="g in gaps" :key="g">{{ t(`listing.fields.${g}`) }}</li></ul>
      </div>
    </div>

    <div class="row">
      <button class="btn" :disabled="busy || invalidUrls" @click="save">
        <span v-if="busy" class="spinner" aria-hidden="true"></span>
        {{ busy ? t("common.saving") : t("listing.save", { locale: t(`listing.locales.${locale}`) }) }}
      </button>
      <span v-if="saved" class="badge badge--success">{{ t("common.saved") }}</span>
      <span v-if="invalidUrls" class="small field__error">{{ t("listing.httpsOnly") }}</span>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { api } from "../../lib/api";
import { useErrorText } from "../../lib/errors";
import { LISTING_LOCALES, listingGaps } from "../../lib/listing";
import { isHttps } from "../../lib/media";
import ImageUpload from "../ImageUpload.vue";

const props = defineProps({
  appId: { type: String, required: true },
  listings: { type: Object, required: true }
});
const emit = defineEmits(["saved"]);
const { t } = useI18n();
const errorText = useErrorText();

const MAX_SHOTS = 5;
const FIELDS = ["tagline", "description", "privacy_url", "support_url", "docs_url", "data_retention_text", "pricing_text"];

const locale = ref("en");
const busy = ref(false);
const saved = ref(false);
const error = ref("");
const drafts = reactive({});

function toDraft(listing) {
  const draft = Object.fromEntries(FIELDS.map((f) => [f, listing?.[f] || ""]));
  draft.screenshots = Array.isArray(listing?.screenshots) ? listing.screenshots.map((s) => (typeof s === "string" ? s : s?.url || "")) : [];
  return draft;
}

for (const code of LISTING_LOCALES) {
  drafts[code] = toDraft(props.listings[code]);
  watch(() => props.listings[code], (listing) => (drafts[code] = toDraft(listing)));
}

const form = computed(() => drafts[locale.value]);
const required = computed(() => locale.value === "en");
const gaps = computed(() => listingGaps(drafts.en));

const invalidUrls = computed(() =>
  [form.value.privacy_url, form.value.support_url, form.value.docs_url, ...form.value.screenshots]
    .filter(Boolean)
    .some((u) => !isHttps(u))
);

async function save() {
  busy.value = true;
  error.value = "";
  saved.value = false;
  const code = locale.value;
  try {
    const payload = { locale: code, ...form.value, screenshots: form.value.screenshots.filter(Boolean) };
    for (const f of FIELDS) payload[f] = String(payload[f] || "").trim() || null;
    await api.saveListing(props.appId, payload);
    emit("saved", code, payload);
    saved.value = true;
    setTimeout(() => (saved.value = false), 2500);
  } catch (e) {
    error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}
</script>

<style scoped>
.listing__shot { margin-bottom: var(--space-2); align-items: flex-start; }
.listing__upload { flex: 1 1 auto; min-width: 0; }
</style>
